import { CountdownTracker } from './tracker';
import { calculateTimeRemaining, TimeRemaining } from './time';

export class Clock {
  trackers: Record<string, CountdownTracker[]>;
  private _animationFrameId: number | null = null;
  private _isCountdownComplete: boolean = false;

  constructor(countdown: Date, callback: () => void) {
    this.trackers = this._createCountdownTrackers(countdown);
    this._beginCountdown(countdown, callback);
  }

  private _createCountdownTrackers(
    countdown: Date
  ): Record<string, CountdownTracker[]> {
    const trackers: Record<string, CountdownTracker[]> = {};
    const timeData = calculateTimeRemaining(countdown);

    Object.keys(timeData)
      .filter(key => key !== 'Total')
      .forEach(key => {
        const value = timeData[key as keyof typeof timeData];
        if (typeof value === 'number') {
          this._createTrackerTemplate(trackers, key, value);
        }
      });

    return trackers;
  }

  private _createTrackerTemplate(
    trackers: Record<string, CountdownTracker[]>,
    key: string,
    value: number
  ): void {
    const digits = String(value).padStart(2, '0').split('');
    trackers[key] = digits.map(digit => new CountdownTracker(parseInt(digit)));
  }

  private _beginCountdown(countdown: Date, callback: () => void) {
    let throttle = 0;

    const updateClock = () => {
      this._animationFrameId = requestAnimationFrame(updateClock);
      if (throttle++ % 10) return;

      const timeData = calculateTimeRemaining(countdown);
      if (typeof timeData.Total === 'number' && timeData.Total < 0) {
        this._handleCountdownCompletion(callback);
        return;
      }

      this._updateCountdownTrackers(timeData);
    };

    updateClock();
  }

  private _handleCountdownCompletion(callback: () => void) {
    if (!this._isCountdownComplete) {
      this._resetCountdownTrackers();
      callback();
      this._isCountdownComplete = true;
    }
    if (this._animationFrameId) {
      cancelAnimationFrame(this._animationFrameId);
    }
  }

  private _resetCountdownTrackers() {
    for (const key in this.trackers) {
      this.trackers[key].forEach(tracker => tracker.update(0));
    }
  }

  private _updateCountdownTrackers(timeData: TimeRemaining) {
    for (const key in this.trackers) {
      if (key in timeData) {
        const digits = String(timeData[key as keyof TimeRemaining])
          .padStart(2, '0')
          .split('');
        digits.forEach((digit, index) => {
          this.trackers[key][index].update(parseInt(digit));
        });
      }
    }
  }
}
